import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { union } from 'lodash';
import { CHANNEL_DEFAULT_FILTERS } from '../sic-lib/defaults';
import { ChannelType } from '../sic-lib/enums/ChannelType';
import { ChannelItemCall } from '../sic-lib/types/ChannelItem';
import { ChannelListFilterItem } from '../sic-lib/types/ChannelListFilterItem';

/*
 *  Conversation Reducer
 *  --------------------
 *  Holds the current contact's conversation history as well as settings applied to a conversation
 *  timeline. Redux is used for this purpose so that real-time updates from signalR, held within
 *  Redux middleware, can access and update the conversation store when applicable.
 */

// --------------
// STATE
export interface ConversationState {
    channels: ChannelListFilterItem[];
    contactComposeMessage: {
        [contactId: number]: {
            subject: string;
            body: string;
            media: string[];
        };
    };
    aiSuggestions: {
        [contactId: number]: {
            [response: number]: {
                text: string;
            };
        };
    };
    suggestReplyChannelIndexId: number | undefined;
    transcriptDrawerChannelItem: ChannelItemCall | undefined;
}

const initialState: ConversationState = {
    channels: [...CHANNEL_DEFAULT_FILTERS],
    contactComposeMessage: {},
    aiSuggestions: {},
    suggestReplyChannelIndexId: undefined,
    transcriptDrawerChannelItem: undefined,
};

//new redux toolkit slice implementation
const conversationSlice = createSlice({
    name: 'conversation',
    initialState,
    reducers: {
        setConversationChannels: (state, action: PayloadAction<{ filters: ChannelType }>) => {
            const { filters } = action.payload;
            state.channels = state.channels.map(channel => {
                if (channel.value === filters) {
                    channel.isActive = !channel.isActive;
                }
                return channel;
            });
        },
        setContactComposeMessage: (
            state,
            action: PayloadAction<{
                contactId: number;
                subject: string;
                body: string;
                media: string[];
            }>,
        ) => {
            const { contactId, subject, body, media } = action.payload;
            state.contactComposeMessage[contactId] = { subject, body, media };
        },
        addMediaToContactComposeMessage: (
            state,
            action: PayloadAction<{
                contactId: number;
                media: string[];
            }>,
        ) => {
            const { contactId, media } = action.payload;

            if (!state.contactComposeMessage[contactId])
                state.contactComposeMessage[contactId] = { subject: '', body: '', media: [] };

            state.contactComposeMessage[contactId].media = union(
                state.contactComposeMessage[contactId].media,
                media,
            );
        },
        removeMediaFromContactComposeMessage: (
            state,
            action: PayloadAction<{
                contactId: number;
                media: string;
            }>,
        ) => {
            const { contactId, media } = action.payload;

            if (!state.contactComposeMessage[contactId])
                state.contactComposeMessage[contactId] = { subject: '', body: '', media: [] };

            state.contactComposeMessage[contactId].media = state.contactComposeMessage[
                contactId
            ].media.filter(mediaItem => mediaItem !== media);
        },
        appendContactComposeBody: (
            state,
            action: PayloadAction<{ contactId: number; body: string }>,
        ) => {
            const { contactId, body } = action.payload;
            state.contactComposeMessage[contactId].body += body;
        },
        appendAiSuggestion: (
            state,
            action: PayloadAction<{
                contactId: number;
                response: number;
                text: string;
            }>,
        ) => {
            const { contactId, response, text } = action.payload;
            if (!state.aiSuggestions[contactId]) {
                state.aiSuggestions[contactId] = {};
            }
            if (!state.aiSuggestions[contactId][response]) {
                state.aiSuggestions[contactId][response] = { text };
            } else {
                state.aiSuggestions[contactId][response].text += text;
            }
        },
        resetAiSuggestion: (state, action: PayloadAction<{ contactId: number }>) => {
            const { contactId } = action.payload;
            state.aiSuggestions[contactId] = {};
        },
        setSuggestReplyChannelIndexId: (
            state,
            action: PayloadAction<{ channelIndexId: number | undefined }>,
        ) => {
            const { channelIndexId } = action.payload;
            state.suggestReplyChannelIndexId = channelIndexId;
        },
        setDrawerTranscript: (
            state,
            action: PayloadAction<{ channelItem: ChannelItemCall | undefined }>,
        ) => {
            const { channelItem } = action.payload;
            state.transcriptDrawerChannelItem = channelItem;
        },
    },
});

export const {
    setConversationChannels,
    setContactComposeMessage,
    appendContactComposeBody,
    appendAiSuggestion,
    resetAiSuggestion,
    setSuggestReplyChannelIndexId,
    setDrawerTranscript,
    addMediaToContactComposeMessage,
    removeMediaFromContactComposeMessage,
} = conversationSlice.actions;

export default conversationSlice.reducer;

// ---------------
// ACTIONS

// /* Timeline Options Actions */
// interface SetConversationChannelsAction {
//     type: 'SET_CONVERSATION_CHANNELS';
//     page: typeof CHANNEL_DEFAULT_PAGE;
//     filters: ChannelType;
// }

// interface SetContactComposeMessageAction {
//     type: 'SET_CONTACT_COMPOSE_MESSAGE';
//     contactId: number;
//     subject: string;
//     body: string;
// }

// interface AppendContactComposeBodyAction {
//     type: 'APPEND_CONTACT_COMPOSE_BODY';
//     contactId: number;
//     body: string;
// }

// interface AppendAiSuggestionAction {
//     type: 'APPEND_AI_SUGGESTION';
//     contactId: number;
//     response: number;
//     text: string;
// }

// interface ResetAiSuggestionAction {
//     type: 'RESET_AI_SUGGESTION';
//     contactId: number;
// }

// interface SetSuggestReplyFiltersAction {
//     type: 'SET_SUGGEST_REPLY_FILTERS';
//     suggestReplyFilters: Partial<DevAiState>;
// }

// export type ConversationActions =
//     | SetConversationChannelsAction
//     | SetContactComposeMessageAction
//     | AppendContactComposeBodyAction
//     | AppendAiSuggestionAction
//     | ResetAiSuggestionAction
//     | SetSuggestReplyFiltersAction;

// // ---------------
// // ACTION CREATORS

// export const actionCreators = {
//     /**
//      * setContactId
//      *
//      * @description
//      * Sets the contactId whose conversation history will be requested by the system,
//      * acts as a reset-all for settings such as page.
//      */
//     setChannels: (filters: ChannelType) => {
//         return {
//             type: 'SET_CONVERSATION_CHANNELS',
//             filters,
//             page: CHANNEL_DEFAULT_PAGE,
//         } as SetConversationChannelsAction;
//     },
//     setContactComposeMessage: (
//         contactId: number,
//         subject: string,
//         body: string,
//     ) => {
//         return {
//             type: 'SET_CONTACT_COMPOSE_MESSAGE',
//             contactId,
//             subject,
//             body,
//         } as SetContactComposeMessageAction;
//     },
//     appendContactComposeBody: (contactId: number, body: string) => {
//         return {
//             type: 'APPEND_CONTACT_COMPOSE_BODY',
//             contactId,
//             body,
//         } as AppendContactComposeBodyAction;
//     },
//     appendAiSuggestion: (contactId: number, response: number, text: string) => {
//         return {
//             type: 'APPEND_AI_SUGGESTION',
//             contactId,
//             response,
//             text,
//         } as AppendAiSuggestionAction;
//     },
//     resetAiSuggestion: (contactId: number) => {
//         return {
//             type: 'RESET_AI_SUGGESTION',
//             contactId,
//         } as ResetAiSuggestionAction;
//     },
//     setSuggestReplyFilters: (suggestReplyFilters: Partial<DevAiState>) => {
//         return {
//             type: 'SET_SUGGEST_REPLY_FILTERS',
//             suggestReplyFilters,
//         } as SetSuggestReplyFiltersAction;
//     },
// };

// // ---------------
// // REDUCERS

// export const reducer = (
//     state: ConversationState | undefined = { ...initialState },
//     action: ConversationActions,
// ): ConversationState => {
//     switch (action.type) {
//         /* Timeline Options Actions */
//         case 'SET_CONVERSATION_CHANNELS':
//             return {
//                 ...state,
//                 ...{
//                     channels: state.channels.map(itm => {
//                         if (itm.value === action.filters) {
//                             itm.isActive = !itm.isActive;
//                         }

//                         return itm;
//                     }),
//                     page: action.page,
//                 },
//             };
//         case 'SET_CONTACT_COMPOSE_MESSAGE':
//             return {
//                 ...state,
//                 ...{
//                     contactComposeMessage: {
//                         [action.contactId]: {
//                             subject: action.subject,
//                             body: action.body,
//                         },
//                     },
//                 },
//             };
//         case 'APPEND_CONTACT_COMPOSE_BODY':
//             return Object.assign({}, state, {
//                 contactComposeMessage: Object.assign(
//                     {},
//                     state.contactComposeMessage,
//                     {
//                         [action.contactId]: Object.assign(
//                             {},
//                             state.contactComposeMessage[action.contactId],
//                             {
//                                 body:
//                                     (state.contactComposeMessage[
//                                         action.contactId
//                                     ]?.body || '') + action.body,
//                             },
//                         ),
//                     },
//                 ),
//             });
//         case 'APPEND_AI_SUGGESTION':
//             return Object.assign({}, state, {
//                 aiSuggestions: Object.assign({}, state.aiSuggestions, {
//                     [action.contactId]: Object.assign(
//                         {},
//                         state.aiSuggestions[action.contactId],
//                         {
//                             [action.response]: Object.assign(
//                                 {},
//                                 state.aiSuggestions[action.contactId]?.[
//                                     action.response
//                                 ],
//                                 {
//                                     text:
//                                         (state.aiSuggestions[
//                                             action.contactId
//                                         ]?.[action.response]?.text || '') +
//                                         action.text,
//                                 },
//                             ),
//                         },
//                     ),
//                 }),
//             });
//         case 'RESET_AI_SUGGESTION':
//             return Object.assign({}, state, {
//                 aiSuggestions: Object.assign({}, state.aiSuggestions, {
//                     [action.contactId]: {},
//                 }),
//             });
//         case 'SET_SUGGEST_REPLY_FILTERS':
//             return {
//                 ...state,
//                 ...{
//                     suggestReplyFilters: {
//                         ...state.suggestReplyFilters,
//                         ...action.suggestReplyFilters,
//                     },
//                 },
//             };
//         default:
//             return state;
//     }
// };

//TODO: migrate everything to new redux toolkit slice implementation
//new redux toolkit slice implementation
